import { GitBranch } from 'lucide-react'
import * as React from 'react'
import {
  useGetAuthzMeQuery,
  useListIngestIntakesQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../../lib/utils'
import { BranchTreeGuides } from '../../../components/Branches/BranchTreeGuides'
import type { BranchTreeGuide } from '../../../components/Branches/branch-tree'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { Badge } from '../../../components/ui/badge'

export type BranchLifecycle = 'active' | 'merged' | 'deleted'

interface BranchRowProps {
  name: string
  detail: string
  isActive: boolean
  onSelect?: () => void
  /** Lifecycle status of the branch row. Non-active rows render muted with a pill. */
  lifecycle?: BranchLifecycle
  /** Optional trailing slot for per-branch actions (e.g. compare/merge/delete menu). */
  actions?: React.ReactNode
  /** Fork-tree indent guides nesting the row under its parent branch. */
  guides?: BranchTreeGuide[]
  origin?: string
}

export function BranchRow({
  name,
  detail,
  isActive,
  onSelect,
  lifecycle = 'active',
  actions,
  guides,
  origin
}: BranchRowProps) {
  const muted = lifecycle !== 'active'
  const canReadIngest = useGetAuthzMeQuery().data?.permissions.includes('ingest/read') === true
  const intakesQuery = useListIngestIntakesQuery(undefined, {
    skip: !canReadIngest || origin !== 'ingest'
  })
  const matchingIntake = intakesQuery.data?.intakes.find((intake) => intake.branchName === name)
  const originLabel =
    origin === 'ingest' ? (matchingIntake ? `intake · ${matchingIntake.name}` : 'ingest') : null

  return (
    <div
      className={cn(
        'flex h-9 w-full items-center border-b border-line transition-colors last:border-b-0',
        muted ? 'opacity-60' : 'hover:bg-st-idle-bg/40',
        isActive && 'bg-brand-soft hover:bg-brand-soft'
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? 'true' : undefined}
        disabled={muted}
        className={cn(
          // pl-3 keeps the row content (and the active-row highlight's
          // leading icon) off the card edge — root rows have no tree
          // guides to inset them.
          'flex h-full min-w-0 flex-1 items-center gap-3 py-1.5 pl-3 pr-2 text-left',
          muted ? 'cursor-default' : 'cursor-pointer'
        )}
      >
        {guides ? <BranchTreeGuides guides={guides} className="-my-1.5" /> : null}
        <GitBranch className={cn('size-3 shrink-0', isActive ? 'text-brand' : 'text-ink-3')} />
        <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,18rem)_minmax(0,1fr)] items-center gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span
              title={name}
              className={cn(
                'min-w-0 truncate whitespace-nowrap font-mono text-sm',
                isActive ? 'text-brand' : 'text-ink'
              )}
            >
              {name}
            </span>
            {originLabel ? <Badge variant="secondary">{originLabel}</Badge> : null}
          </span>
          <span title={detail} className="min-w-0 truncate whitespace-nowrap text-xs text-ink-3">
            {detail}
          </span>
        </span>
        <span className="ml-auto flex min-w-18 shrink-0 items-center justify-end">
          {isActive ? (
            <StatusPill status="run">active</StatusPill>
          ) : lifecycle === 'merged' ? (
            <StatusPill status="info">merged</StatusPill>
          ) : lifecycle === 'deleted' ? (
            <StatusPill status="down">deleted</StatusPill>
          ) : null}
        </span>
      </button>
      {actions ? <div className="flex shrink-0 items-center pl-1">{actions}</div> : null}
    </div>
  )
}
